import { FEEDBACK_TARGET_KIND, assertNever, type Target } from './types';

// This module is the authoritative feedback tag taxonomy. The backend records
// whatever tags it receives verbatim and does NOT validate tag values, so add or
// change tags here — there is no backend allow-list to keep in sync.

/** Enumerates the stable submitted identifiers used in feedback payloads. */
export const FEEDBACK_TAG_ID = {
  BUG: 'bug',
  CONFUSING: 'confusing',
  MISSING_FEATURE: 'missing_feature',
  PERFORMANCE: 'performance',
  PRAISE: 'praise',
  INACCURATE: 'inaccurate',
  INCOMPLETE: 'incomplete',
  OUTDATED: 'outdated',
  OFF_TOPIC: 'off_topic',
  WELL_DONE: 'well_done',
  NOT_RELEVANT: 'not_relevant',
  DUPLICATE: 'duplicate',
  LOW_QUALITY: 'low_quality',
  INTERESTING: 'interesting',
  WRONG_DETAILS: 'wrong_details',
  PRICE_ISSUE: 'price_issue',
  AVAILABILITY: 'availability',
  NOT_WHAT_I_WANTED: 'not_what_i_wanted',
  GOOD_MATCH: 'good_match',
  UNCLEAR: 'unclear',
  HELPFUL: 'helpful',
} as const;

/** Stores the union of stable submitted identifiers used in feedback payloads. */
export type FeedbackTagId = (typeof FEEDBACK_TAG_ID)[keyof typeof FEEDBACK_TAG_ID];

/** Describes a feedback tag's stable submitted identifier and human-readable label. */
export type FeedbackTagOption = {
  /** Stores the stable identifier submitted in feedback payloads. */
  id: FeedbackTagId;
  /** Stores the human-readable Title Case label shown in the UI. */
  label: string;
};

/** Maps each target kind to its current draft feedback tag taxonomy. */
const TAGS_BY_TARGET_KIND = {
  general: [
    { id: FEEDBACK_TAG_ID.BUG, label: 'Bug' },
    { id: FEEDBACK_TAG_ID.CONFUSING, label: 'Confusing' },
    { id: FEEDBACK_TAG_ID.MISSING_FEATURE, label: 'Missing Feature' },
    { id: FEEDBACK_TAG_ID.PERFORMANCE, label: 'Performance' },
    { id: FEEDBACK_TAG_ID.PRAISE, label: 'Praise' },
  ],
  [FEEDBACK_TARGET_KIND.DOC]: [
    { id: FEEDBACK_TAG_ID.INACCURATE, label: 'Inaccurate' },
    { id: FEEDBACK_TAG_ID.INCOMPLETE, label: 'Incomplete' },
    { id: FEEDBACK_TAG_ID.OUTDATED, label: 'Outdated' },
    { id: FEEDBACK_TAG_ID.OFF_TOPIC, label: 'Off Topic' },
    { id: FEEDBACK_TAG_ID.WELL_DONE, label: 'Well Done' },
  ],
  [FEEDBACK_TARGET_KIND.FEED_ITEM]: [
    { id: FEEDBACK_TAG_ID.NOT_RELEVANT, label: 'Not Relevant' },
    { id: FEEDBACK_TAG_ID.DUPLICATE, label: 'Duplicate' },
    { id: FEEDBACK_TAG_ID.LOW_QUALITY, label: 'Low Quality' },
    { id: FEEDBACK_TAG_ID.INTERESTING, label: 'Interesting' },
  ],
  [FEEDBACK_TARGET_KIND.PRODUCT]: [
    { id: FEEDBACK_TAG_ID.WRONG_DETAILS, label: 'Wrong Details' },
    { id: FEEDBACK_TAG_ID.PRICE_ISSUE, label: 'Price Issue' },
    { id: FEEDBACK_TAG_ID.AVAILABILITY, label: 'Availability' },
    { id: FEEDBACK_TAG_ID.NOT_WHAT_I_WANTED, label: 'Not What I Wanted' },
    { id: FEEDBACK_TAG_ID.GOOD_MATCH, label: 'Good Match' },
  ],
  [FEEDBACK_TARGET_KIND.TOPIC]: [
    { id: FEEDBACK_TAG_ID.UNCLEAR, label: 'Unclear' },
    { id: FEEDBACK_TAG_ID.INCOMPLETE, label: 'Incomplete' },
    { id: FEEDBACK_TAG_ID.INACCURATE, label: 'Inaccurate' },
    { id: FEEDBACK_TAG_ID.HELPFUL, label: 'Helpful' },
  ],
} as const satisfies Record<
  'general' | (typeof FEEDBACK_TARGET_KIND)[keyof typeof FEEDBACK_TARGET_KIND],
  readonly FeedbackTagOption[]
>;

/** Returns the feedback tags offered for the given target, or general tags for null targets. */
export function tagsForTarget(target: Target): FeedbackTagOption[] {
  if (target === null) {
    return TAGS_BY_TARGET_KIND.general.map((tag) => ({ ...tag }));
  }

  switch (target.kind) {
    case FEEDBACK_TARGET_KIND.DOC:
      return TAGS_BY_TARGET_KIND.doc.map((tag) => ({ ...tag }));
    case FEEDBACK_TARGET_KIND.FEED_ITEM:
      return TAGS_BY_TARGET_KIND.feed_item.map((tag) => ({ ...tag }));
    case FEEDBACK_TARGET_KIND.PRODUCT:
      return TAGS_BY_TARGET_KIND.product.map((tag) => ({ ...tag }));
    case FEEDBACK_TARGET_KIND.TOPIC:
      return TAGS_BY_TARGET_KIND.topic.map((tag) => ({ ...tag }));
    default:
      return assertNever(target);
  }
}
